import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { devDebug } from "@/lib/debug";

/**
 * QR scanner hook using html5-qrcode. Manages start/stop and cleanup on unmount.
 *
 *   const { scanning, start, stop } = useQrScanner({ elementId, onDecode, onError });
 */
export function useQrScanner({ elementId, onDecode, onError }) {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const stop = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (err) {
      devDebug("Scanner stop cleanup failed (safe to ignore)", err);
    }
    scannerRef.current = null;
    setScanning(false);
  };

  const start = () => {
    setScanning(true);
    // Wait for the target div to be mounted
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(elementId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            await stop();
            if (onDecode) onDecode(decoded);
          },
          () => {}
        );
      } catch (err) {
        setScanning(false);
        if (onError) onError(err);
      }
    }, 100);
  };

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scanning, start, stop };
}
